import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthLayout } from '../components/AuthLayout';
import { PersonalDetailsForm } from '../components/PersonalDetailsForm';
import { LoginForm } from '../components/LoginForm';
import { useNavigate } from 'react-router-dom';
import { paths } from 'routes';
import { useQueryParams } from 'hook/useQueryParams';
import { PaperCard } from 'components/Card';
import { Button, Stack, Typography } from '@mui/material';
import { RedMessageBar } from 'components/RedMessageBar';

import { acceptInvitationNewAccount, acceptInvitationExistingAccount, lookupInvitation } from '../api/acceptInvitation';
import { useAuth } from 'lib/auth';

export const AcceptInvitation = () => {
  const { t } = useTranslation();
  const { account, refreshSession } = useAuth();
  const navigate = useNavigate();
  const queryParams = useQueryParams();
  const token = queryParams.token;
  const [error, setError ] = useState(null);
  const [invitation, setInvitation] = useState(null);
  const [accepting, setAccepting] = useState(false);

  const [values, setValues] = useState({
    firstName: '',
    lastName: '',
    phoneNumber: '',
    companyEmail: '',
    password: '',
    confirmPassword: '',
  });

  const handleChange = input => e => {
    setValues({ ...values, [input]: e.target.value });
  }

  const finishAcceptance = async (accept) => {
    setAccepting(true);
    try {
      const response = await accept(values, token);
      await refreshSession(response?.data?.user_id);
      navigate(paths.homepage);
    } catch (error) {
      setAccepting(false);
      setError(error.message);
    }
  };

  const onSubmitNewAccount = () => finishAcceptance(acceptInvitationNewAccount);

  const onSubmitExistingAccount = () => finishAcceptance(acceptInvitationExistingAccount);

  useEffect(() => {
    const initialAction = async () => {
      try {
        const result = await lookupInvitation(token);
        setInvitation(result);
        setValues((current) => ({ ...current, firstName: result.first_name, lastName: result.last_name, companyEmail: result.email }));
      } catch (error) {
        setError(error.code === 'invitation_invalid' ? t('website:auth.acceptInvitation.tokenExpired') : error.message);
      }
    }

    initialAction();
  }, []);  

  return (
    <AuthLayout title={t('website:auth.acceptInvitation.pageTitle')}>
    {error && (
      <PaperCard px={{ xs: 2, sm: 4 }} width={{ xs: 1, sm: 368 }} maxWidth={368} sx={{ my: 5 }}>
        <Stack>
          <Typography variant="h5" sx={{ mb: 3, textAlign: "center" }}>
            {t('website:auth.acceptInvitation.heading')}
          </Typography>
          <RedMessageBar message={error} />
          <Button size="large" variant="contained" color="primary" sx={{ mt: 2, mb: 0.5 }} href={paths.homepage}>
            {t('website:auth.acceptInvitation.returnToHomeButton')}
          </Button>
        </Stack>
      </PaperCard>
    )}
    {(!error && invitation && account) && (
      <PaperCard px={{ xs: 2, sm: 4 }} width={{ xs: 1, sm: 368 }} maxWidth={368} sx={{ my: 5 }}>
        <Stack spacing={2}>
          <Typography variant="h5" sx={{ textAlign: "center" }}>
            {t('website:auth.acceptInvitation.heading')}
          </Typography>
          <Typography variant="body1">
            {t('website:auth.membershipPending.invitation', { company: invitation.company_name })}
          </Typography>
          <Button size="large" variant="contained" color="primary" disabled={accepting} onClick={onSubmitExistingAccount}>
            {t('website:auth.membershipPending.acceptButton')}
          </Button>
        </Stack>
      </PaperCard>
    )}
    {(!error && !account && invitation?.new_account === true) && (
      <PersonalDetailsForm 
        nextStep={ onSubmitNewAccount }
        handleChange={ handleChange }
        values={ values }
        acceptInvitation = {true}
      />
    )}
    {(!error && !account && invitation?.new_account === false) && (
      <LoginForm 
        values={ values }
        registrationFlow={ true }
        existingUser={ true }
        onSuccess={ onSubmitExistingAccount }
        msg={t('website:auth.acceptInvitation.existingAccountMessage')}
        acceptInvitation={ true }
      />
    )}
    </AuthLayout>
  );
};
